// Utilitaires export Excel (CSV) et PDF pour Schoolizy

// ── Export CSV (compatible Excel) ────────────────────
export function exportCSV(data, filename = 'export') {
  if (!data || !data.length) return;
  const headers = Object.keys(data[0]);
  const rows = data.map((row) =>
    headers.map((h) => {
      const val = row[h] ?? '';
      const str = String(val).replace(/"/g, '""');
      return str.includes(',') || str.includes('"') || str.includes('\n') ? `"${str}"` : str;
    }).join(',')
  );
  const csv = '\uFEFF' + [headers.join(','), ...rows].join('\n'); // BOM pour Excel
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${filename}_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click(); URL.revokeObjectURL(url);
}

// ── Export PDF (impression navigateur) ───────────────
export function exportPDF(title, headers, rows, filename = 'export') {
  const w = window.open('', '_blank');
  const tableRows = rows.map((row) =>
    `<tr>${row.map((cell) => `<td>${cell ?? '—'}</td>`).join('')}</tr>`
  ).join('');
  w.document.write(`<!DOCTYPE html><html lang="fr"><head>
    <meta charset="utf-8">
    <title>${title}</title>
    <style>
      body { font-family: Arial, sans-serif; font-size: 11px; margin: 20px; color: #1e293b; }
      h2 { font-size: 14px; margin-bottom: 4px; }
      p { font-size: 10px; color: #64748b; margin-bottom: 12px; }
      table { width: 100%; border-collapse: collapse; }
      th { background: #0f2744; color: white; padding: 7px 10px; text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: .04em; }
      td { padding: 6px 10px; border-bottom: 0.5px solid #e2e8f0; font-size: 11px; }
      tr:nth-child(even) td { background: #f8fafc; }
      @media print { @page { margin: 12mm; } }
    </style></head><body>
    <h2>${title}</h2>
    <p>Exporté le ${new Date().toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} — Groupe Scolaire La Providence</p>
    <table>
      <thead><tr>${headers.map((h) => `<th>${h}</th>`).join('')}</tr></thead>
      <tbody>${tableRows}</tbody>
    </table>
    <script>window.addEventListener('load', () => window.print());<\/script>
    </body></html>`);
  w.document.close();
}

// ── Helpers par module ────────────────────────────────
export function exportElevesCSV(students) {
  exportCSV(students.map((s) => ({
    Matricule: s.matricule,
    Nom: s.lastName,
    Prénom: s.firstName,
    Sexe: s.gender === 'M' ? 'Masculin' : 'Féminin',
    'Date naissance': s.birthDate ? new Date(s.birthDate).toLocaleDateString('fr-FR') : '',
    'Lieu naissance': s.birthPlace || '',
    Classe: s.enrollments?.[0]?.class?.name || '',
    'Groupe sanguin': s.bloodGroup || '',
    Électrophorèse: s.phenotype || '',
    Statut: s.status || '',
  })), 'eleves');
}

export function exportElevesPDF(students) {
  exportPDF(
    'Liste des élèves',
    ['Matricule', 'Nom', 'Prénom', 'Sexe', 'Naissance', 'Classe', 'Statut'],
    students.map((s) => [
      s.matricule,
      s.lastName,
      s.firstName,
      s.gender === 'M' ? 'M' : 'F',
      s.birthDate ? new Date(s.birthDate).toLocaleDateString('fr-FR') : '—',
      s.enrollments?.[0]?.class?.name || '—',
      s.status || '—',
    ]),
    'eleves'
  );
}

export function exportNotesCSV(evaluation, rows) {
  exportCSV(rows.map((r) => ({
    Matricule: r.matricule,
    Nom: r.name?.split(' ')[0] || '',
    Prénom: r.name?.split(' ').slice(1).join(' ') || '',
    Note: r.absent ? 'ABS' : (r.note ?? ''),
    Absent: r.absent ? 'Oui' : 'Non',
    Appréciation: r.appreciation || '',
  })), `notes_${evaluation?.title?.replace(/\s+/g, '_') || 'eval'}`);
}

export function exportNotesPDF(evaluation, rows) {
  exportPDF(
    `${evaluation?.title || 'Évaluation'} — ${evaluation?.class?.name || ''} — Barème /${evaluation?.baremeTotal || 20}`,
    ['Matricule', 'Nom', 'Prénom', 'Note', 'Absent', 'Appréciation'],
    rows.map((r) => [
      r.matricule,
      r.name?.split(' ')[0] || '',
      r.name?.split(' ').slice(1).join(' ') || '',
      r.absent ? 'ABS' : (r.note ?? '—'),
      r.absent ? 'Oui' : 'Non',
      r.appreciation || '—',
    ]),
    `notes_${evaluation?.id?.slice(0, 8) || 'eval'}`
  );
}

export function exportPaiementsCSV(rows) {
  exportCSV(rows.map((r) => ({
    Numéro: r.numero,
    Étudiant: r.nom,
    Classe: r.classe,
    'Montant assigné': r.montantAssigne,
    'Total réglé': r.totalRegle,
    'Reste à payer': r.resteAPayer,
    '% Payé': `${r.pctPaye}%`,
    Statut: r.statut,
  })), 'paiements');
}

export function exportStaffCSV(staff) {
  exportCSV(staff.map((s) => ({
    Matricule: s.matricule,
    Nom: s.lastName,
    Prénom: s.firstName,
    Type: s.type,
    Statut: s.status,
    Téléphone: s.phone || '',
    Email: s.email || '',
    'Date prise de service': s.hireDate ? new Date(s.hireDate).toLocaleDateString('fr-FR') : '',
  })), 'personnel');
}
